import React, { forwardRef } from 'react';
import Spinner from '@components/ui/Spinner/Spinner';
import styles from './Button.module.css';

export type ButtonVariant =
  'primary' | 'inverse' | 'secondary' | 'brand' | 'ghost' | 'text' | 'icon';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  /** Omit to keep the browser's control padding and font size. */
  size?: ButtonSize;
  /** Sets aria-busy and disabled, and shows a spinner in place of the label. */
  loading?: boolean;
  loadingLabel?: React.ReactNode;
  spinner?: React.ReactNode;
  /** Rendered before the children, with no wrapper. */
  icon?: React.ReactNode;
  /** Square padding for a button whose only content is an icon; give it an aria-label. */
  iconOnly?: boolean;
  fullWidth?: boolean;
  /** Keeps the browser's control font instead of the body typeface. */
  nativeFont?: boolean;
}

const Button = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      variant = 'primary',
      size,
      loading = false,
      loadingLabel,
      spinner,
      icon,
      iconOnly = false,
      fullWidth = false,
      nativeFont = false,
      type = 'button',
      disabled = false,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const classes = [
      styles.button,
      styles[variant],
      size && styles[size],
      !nativeFont && styles.bodyFont,
      iconOnly && styles.iconOnly,
      fullWidth && styles.fullWidth,
      className,
    ];

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={classes.filter(Boolean).join(' ')}
        {...rest}
      >
        {loading ? (
          <span className={styles.loadingContent}>
            {spinner ?? <Spinner aria-hidden={loadingLabel ? true : undefined} />}
            {loadingLabel && <span>{loadingLabel}</span>}
          </span>
        ) : (
          <>
            {icon}
            {children}
          </>
        )}
      </button>
    );
  },
);

Button.displayName = 'Button';

export default Button;
